/**
 * Nordhav sections for Horizon.
 * - Product gallery (arrows, thumbnails, swipe)
 * - Pack (variant) and purchase option (one-time / subscription) switching
 * - Add to cart, then straight to checkout (or the cart)
 * - Footer "Cookie settings" link
 */

const READY = 'nhReady';

/* ------------------------------------------------------------------ */
/* Gallery                                                             */
/* ------------------------------------------------------------------ */
function initGallery(root) {
  const main = root.querySelector('[data-nh-main]');
  if (!main) return null;

  const slides = Array.from(main.querySelectorAll('[data-nh-slide]'));
  const thumbs = Array.from(root.querySelectorAll('[data-nh-thumb]'));
  if (!slides.length) return null;

  let index = Math.max(
    0,
    slides.findIndex((slide) => !slide.hidden)
  );

  function show(next) {
    index = (next + slides.length) % slides.length;
    slides.forEach((slide, i) => {
      slide.hidden = i !== index;
    });
    thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
  }

  function warm() {
    slides.forEach((slide) => {
      if (slide.loading === 'lazy') slide.loading = 'eager';
    });
  }

  main.querySelector('[data-nh-prev]')?.addEventListener('click', () => show(index - 1));
  main.querySelector('[data-nh-next]')?.addEventListener('click', () => show(index + 1));
  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));

  // Load the other images once the shopper shows interest in the gallery.
  root.querySelector('[data-nh-gallery]')?.addEventListener('pointerenter', warm, { once: true });
  root.querySelector('[data-nh-gallery]')?.addEventListener('touchstart', warm, { once: true, passive: true });

  // Basic swipe on touch screens.
  let startX = null;
  let startY = null;
  main.addEventListener(
    'touchstart',
    (event) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    },
    { passive: true }
  );
  main.addEventListener(
    'touchend',
    (event) => {
      if (startX === null) return;
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) show(index + (dx < 0 ? 1 : -1));
    },
    { passive: true }
  );

  return {
    showMedia(mediaId) {
      if (!mediaId) return;
      const i = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (i >= 0) show(i);
    },
  };
}

/* ------------------------------------------------------------------ */
/* Product form                                                        */
/* ------------------------------------------------------------------ */
function initProduct(root) {
  if (root.dataset[READY]) return;
  const dataEl = root.querySelector('[data-nh-json]');
  const form = root.querySelector('[data-nh-form]');
  if (!dataEl || !form) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (error) {
    console.error('Nordhav: product data could not be read', error);
    return;
  }
  root.dataset[READY] = 'true';

  const gallery = initGallery(root);
  const idInput = form.querySelector('[data-nh-id]');
  const optionSets = Array.from(form.querySelectorAll('[data-nh-option]'));
  const planRadios = Array.from(form.querySelectorAll('[data-nh-plan-radio]'));
  const button = form.querySelector('[data-nh-submit]');
  const message = form.querySelector('[data-nh-msg]');
  const subNote = form.querySelector('[data-nh-sub-note]');
  const els = {
    now: root.querySelector('[data-nh-now]'),
    was: root.querySelector('[data-nh-was]'),
    save: root.querySelector('[data-nh-save]'),
    unit: root.querySelector('[data-nh-unit]'),
  };

  let variant = data.variants.find((v) => v.id === data.initialVariant) || data.variants[0];
  let busy = false;

  const fill = (template, token, value) => String(template || '').split(token).join(value);
  const compareHtml = (full) => (data.compareFormat ? fill(data.compareFormat, '[price]', full) : `<s>${full}</s>`);

  function selectedOptions() {
    return optionSets.map((set) => set.querySelector('input:checked')?.value);
  }

  function findVariant(options) {
    return data.variants.find((v) => v.options.every((value, i) => options[i] === undefined || value === options[i]));
  }

  function currentPlanId() {
    const checked = planRadios.find((radio) => radio.checked);
    return checked && checked.value ? checked.value : null;
  }

  function priceFor(v, planId) {
    if (!v) return null;
    return planId ? v.plans[planId] || null : v.once;
  }

  function setPrice(container, info, isCard) {
    if (!info || (isCard && !container)) return;
    const priceEl = isCard ? container.querySelector('[data-nh-cp]') : els.now;
    const wasEl = isCard ? container.querySelector('[data-nh-cw]') : els.was;
    const saveEl = isCard ? container.querySelector('[data-nh-cs]') : els.save;
    const unitEl = isCard ? container.querySelector('[data-nh-cu]') : els.unit;
    const hasSaving = info.save > 0;

    if (priceEl) priceEl.innerHTML = info.price;
    if (wasEl) {
      wasEl.innerHTML = hasSaving ? compareHtml(info.full) : '';
      wasEl.hidden = !hasSaving;
    }
    if (saveEl) {
      saveEl.textContent = fill(data.saveLabel, '[percent]', info.save);
      saveEl.hidden = !hasSaving;
    }
    if (unitEl) {
      unitEl.innerHTML = info.unit ? fill(isCard ? data.unitCard : data.unitMain, '[price]', info.unit) : '';
      unitEl.hidden = !info.unit;
    }
  }

  function updateCards() {
    planRadios.forEach((radio) => {
      const card = radio.closest('.nh-plan');
      const info = priceFor(variant, radio.value || null);
      if (!card) return;
      card.hidden = !info;
      radio.disabled = !info;
      if (info) setPrice(card, info, true);
    });

    // If the selected option no longer applies to this variant, fall back to the first available one.
    if (planRadios.length && !planRadios.some((radio) => radio.checked && !radio.disabled)) {
      const first = planRadios.find((radio) => !radio.disabled);
      if (first) first.checked = true;
    }
  }

  function updateAvailability() {
    const selected = selectedOptions();
    optionSets.forEach((set, setIndex) => {
      set.querySelectorAll('input').forEach((input) => {
        const options = selected.slice();
        options[setIndex] = input.value;
        const match = findVariant(options);
        const label = set.querySelector(`label[for="${input.id}"]`);
        if (label) label.classList.toggle('is-unavailable', !match || !match.available);
      });
      const valueEl = set.querySelector('[data-nh-option-value]');
      if (valueEl) {
        const useSku = data.optionLabel !== 'value' && optionSets.length === 1 && variant && variant.sku;
        valueEl.textContent = useSku ? variant.sku : selected[setIndex] || '';
      }
    });
  }

  function updateButton() {
    if (!button || busy) return;
    if (!variant) {
      button.disabled = true;
      button.textContent = data.text.unavailable;
    } else if (!variant.available) {
      button.disabled = true;
      button.textContent = data.text.soldOut;
    } else {
      button.disabled = false;
      button.textContent = data.text.button;
    }
  }

  function render() {
    if (idInput && variant) idInput.value = variant.id;
    updateCards();
    const planId = currentPlanId();
    setPrice(null, priceFor(variant, planId), false);
    if (subNote) subNote.hidden = !planId;
    updateAvailability();
    updateButton();
  }

  function hideMessage() {
    if (!message) return;
    message.hidden = true;
    message.textContent = '';
    message.classList.remove('nh-msg--error');
  }

  function showMessage(text, isError) {
    if (!message) return;
    message.textContent = text;
    message.classList.toggle('nh-msg--error', Boolean(isError));
    message.hidden = false;
  }

  optionSets.forEach((set) =>
    set.addEventListener('change', () => {
      variant = findVariant(selectedOptions()) || null;
      hideMessage();
      render();
      if (variant) {
        gallery?.showMedia(variant.media);
        if (data.template === 'product') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
      }
    })
  );

  planRadios.forEach((radio) => radio.addEventListener('change', render));

  form.querySelectorAll('[data-nh-plan-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const radio = select.closest('.nh-plan')?.querySelector('[data-nh-plan-radio]');
      if (!radio) return;
      radio.value = select.value;
      radio.checked = true;
      render();
    });
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy || !variant || !variant.available) return;

    const planId = currentPlanId();
    const item = { id: variant.id, quantity: 1 };
    if (planId) item.selling_plan = Number(planId);

    busy = true;
    hideMessage();
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    // Let the theme (cart icon, analytics) know a line is being added, the same way Horizon does.
    let CartEvent = null;
    let deferred = null;
    try {
      ({ CartLinesUpdateEvent: CartEvent } = await import('@shopify/events'));
      deferred = CartEvent.createPromise();
      form.dispatchEvent(
        new CartEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      deferred = null;
    }

    try {
      const response = await fetch(`${data.routes.cartAdd}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [item] }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || data.text.error);
      }

      if (deferred) {
        try {
          const cart = await fetch(`${data.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
            r.json()
          );
          deferred.resolve({
            cart: CartEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'nh-product',
              sourceId: root.id,
              itemCount: 1,
              productId: String(data.productId),
              didError: false,
            },
          });
        } catch (error) {
          /* Analytics only — never block checkout. */
        }
      }

      const base = String(data.routes.root || '/').replace(/\/$/, '');
      window.location.assign(data.after === 'cart' ? data.routes.cart : `${base}/checkout`);
    } catch (error) {
      if (deferred) {
        try {
          deferred.reject(error);
        } catch (rejectError) {
          /* ignore */
        }
      }
      busy = false;
      if (button) button.removeAttribute('aria-busy');
      showMessage(error && error.message ? error.message : data.text.error, true);
      updateButton();
    }
  });

  render();
}

/* ------------------------------------------------------------------ */
/* Footer: cookie preferences link                                     */
/* ------------------------------------------------------------------ */
function initCookieLinks(scope) {
  scope.querySelectorAll('[data-nh-cookie]').forEach((link) => {
    if (link.dataset[READY]) return;
    link.dataset[READY] = 'true';
    link.addEventListener('click', (event) => {
      const banner = window.privacyBanner;
      if (banner && typeof banner.showPreferences === 'function') {
        event.preventDefault();
        banner.showPreferences();
      }
    });
  });
}

/* ------------------------------------------------------------------ */
/* Boot                                                                */
/* ------------------------------------------------------------------ */
function boot(scope = document) {
  scope.querySelectorAll('[data-nh-product]').forEach(initProduct);
  initCookieLinks(scope);
}

boot();

// Theme editor: re-initialise sections when they are re-rendered.
document.addEventListener('shopify:section:load', (event) => boot(event.target));
